// Orders module — main 3-column page.
// Inspired by Linear/Stripe Dashboard/Gorgias: dense but airy, premium typography,
// fast keyboard nav, sticky search, status pills.
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import {
  listOrders, getOrder, getOrderCustomer, syncOrders, getSyncStatus,
} from "@/lib/orders";
import OrdersList from "@/pages/orders/OrdersList";
import OrderDetail from "@/pages/orders/OrderDetail";
import CustomerPanel from "@/pages/orders/CustomerPanel";
import { Button } from "@/components/ui/button";
import { useRealtimeEvent } from "@/lib/realtime-context";

export default function OrdersPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [orders, setOrders] = useState([]);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [nextCursor, setNextCursor] = useState(null);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [activeOrderId, setActiveOrderId] = useState(searchParams.get("id"));
  const [orderData, setOrderData] = useState(null); // {order, notes, activity}
  const [customer, setCustomer] = useState(null);
  const [orderLoading, setOrderLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);

  // ---- list reload (search/filter changes) ----
  const reloadList = useCallback(async () => {
    setLoadingList(true);
    try {
      const r = await listOrders({ search, filter, limit: 50 });
      setOrders(r.items || []);
      setNextCursor(r.next_cursor);
    } catch (e) {
      toast.error("Eroare la încărcarea comenzilor");
    } finally {
      setLoadingList(false);
    }
  }, [search, filter]);

  useEffect(() => {
    const t = setTimeout(reloadList, search ? 280 : 0);
    return () => clearTimeout(t);
  }, [reloadList, search]);

  // ---- pagination ----
  const loadMore = useCallback(async () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const r = await listOrders({ search, filter, cursor: nextCursor, limit: 50 });
      setOrders((prev) => [...prev, ...(r.items || [])]);
      setNextCursor(r.next_cursor);
    } finally {
      setLoadingMore(false);
    }
  }, [nextCursor, loadingMore, search, filter]);

  // ---- sync status ----
  const reloadSyncStatus = useCallback(async () => {
    try { setSyncStatus(await getSyncStatus()); } catch {}
  }, []);
  useEffect(() => { reloadSyncStatus(); }, [reloadSyncStatus]);

  // ---- realtime: refresh list whenever a Shopify webhook hits the broker ----
  const reloadListRef = useRef(reloadList);
  useEffect(() => { reloadListRef.current = reloadList; }, [reloadList]);
  const onRealtimeOrderEvent = useCallback(() => {
    if (onRealtimeOrderEvent._t) clearTimeout(onRealtimeOrderEvent._t);
    onRealtimeOrderEvent._t = setTimeout(() => { reloadListRef.current?.(); reloadSyncStatus(); }, 400);
  }, [reloadSyncStatus]);
  useRealtimeEvent("order.created", onRealtimeOrderEvent);
  useRealtimeEvent("order.updated", onRealtimeOrderEvent);
  useRealtimeEvent("order.cancelled", onRealtimeOrderEvent);
  useRealtimeEvent("fulfillment.updated", onRealtimeOrderEvent);

  const handleSync = async (full = false) => {
    setSyncing(true);
    toast.loading(full ? "Sincronizez TOATE comenzile..." : "Sincronizez ultimele 30 zile...", { id: "sync" });
    try {
      const r = await syncOrders(full);
      if (r.ok) {
        toast.success(`Sincronizat ${r.synced} comenzi`, { id: "sync" });
        await reloadList();
        await reloadSyncStatus();
      } else {
        toast.error(`Sync eșuat: ${r.error}`, { id: "sync" });
      }
    } catch (e) {
      toast.error("Sync eșuat", { id: "sync" });
    } finally {
      setSyncing(false);
    }
  };

  // ---- selection ----
  const openOrder = useCallback(async (id) => {
    setActiveOrderId(id);
    setSearchParams((p) => {
      const next = new URLSearchParams(p);
      next.set("id", id);
      return next;
    });
    setOrderLoading(true);
    setOrderData(null);
    setCustomer(null);
    try {
      const [o, c] = await Promise.all([getOrder(id), getOrderCustomer(id)]);
      setOrderData(o);
      setCustomer(c);
    } catch (e) {
      toast.error("Comandă inaccesibilă");
    } finally {
      setOrderLoading(false);
    }
  }, [setSearchParams]);

  // Auto-open initial order if present in URL
  useEffect(() => {
    const id = searchParams.get("id");
    if (id && id !== activeOrderId) openOrder(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // After refresh: if no order selected and we have orders, optionally open first
  // (skip — user can choose).

  const refreshOrder = useCallback(async () => {
    if (!activeOrderId) return;
    try {
      const [o, c] = await Promise.all([
        getOrder(activeOrderId),
        getOrderCustomer(activeOrderId),
      ]);
      setOrderData(o);
      setCustomer(c);
    } catch {}
  }, [activeOrderId]);

  // Build a "conversation"-shape so the shared CustomerPanel renders correctly
  const conversation = useMemo(() => {
    if (!orderData?.order) return null;
    const phone = orderData.order.phone;
    return {
      jid: "order:" + orderData.order.id,
      phone,
      displayPhone: phone,
      contactName: orderData.order.customer_name,
    };
  }, [orderData?.order]);

  return (
    <div
      className="h-[calc(100vh-7rem)] -m-4 sm:-m-6 lg:-m-8 grid grid-cols-12 bg-background overflow-hidden border-t border-border"
      data-testid="orders-page"
    >
      {/* Left — list */}
      <div className="col-span-12 md:col-span-4 lg:col-span-3 border-r border-border flex flex-col min-h-0">
        <div className="px-4 py-3 border-b border-border flex items-center justify-between gap-2 shrink-0">
          <div className="min-w-0">
            <div className="font-display font-semibold text-base">Comenzi</div>
            <div className="text-[11px] text-muted-foreground">
              {syncStatus?.total_orders ?? 0} cached
            </div>
          </div>
          <Button
            size="sm"
            variant="outline"
            disabled={syncing}
            onClick={() => handleSync(false)}
            className="h-8"
            data-testid="orders-sync-btn"
          >
            {syncing ? (
              <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
            ) : (
              <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
            )}
            Sync
          </Button>
        </div>

        <OrdersList
          orders={orders}
          loading={loadingList}
          loadingMore={loadingMore}
          activeOrderId={activeOrderId}
          onOpen={openOrder}
          search={search}
          setSearch={setSearch}
          filter={filter}
          setFilter={setFilter}
          onLoadMore={loadMore}
          hasMore={!!nextCursor}
        />
      </div>

      {/* Middle — order detail */}
      <div className="hidden md:flex md:col-span-8 lg:col-span-6 flex-col min-h-0 bg-secondary/15 overflow-hidden">
        {orderLoading && !orderData ? (
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : orderData?.order ? (
          <OrderDetail
            data={orderData}
            onRefresh={refreshOrder}
          />
        ) : (
          <EmptyState />
        )}
      </div>

      {/* Right — customer profile (reused from WhatsApp) */}
      <div className="hidden lg:flex lg:col-span-3 border-l border-border flex-col min-h-0">
        <CustomerPanel
          conversation={conversation}
          customer={customer}
          loading={orderLoading}
        />
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground p-8 text-center">
      <div className="h-14 w-14 rounded-2xl bg-secondary/60 mb-3 flex items-center justify-center">
        <Loader2 className="h-5 w-5" />
      </div>
      <div className="font-display font-semibold text-foreground">Selectează o comandă</div>
      <p className="text-sm mt-1.5 max-w-sm">
        Caută după nume, telefon, email, număr comandă sau AWB pentru a deschide rapid o comandă.
      </p>
    </div>
  );
}
